import { Section, Card } from "@/components/Section";
import { CalendarDays, Clock, Video, ShieldCheck, MousePointerClick, MessageSquare, Sparkles, ArrowRight, Users } from "lucide-react";

const ZOOM_REG_URL = "https://us06web.zoom.us/meeting/register/La-NAZ3nTbGGy7_xDvzFoQ";
const EVENT_DATE = "Tuesday, October 27, 2026";
const EVENT_TIME = "6:00 to 7:00 p.m. Eastern";

const ReserveButton = ({ testid, size = "lg", className = "" }) => {
  const sizing = size === "lg"
    ? "px-9 py-4 text-base"
    : "px-7 py-3.5 text-sm";
  return (
    <a
      href={ZOOM_REG_URL}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={testid}
      className={`btn-gold inline-flex items-center gap-2 rounded-full font-semibold uppercase tracking-[0.18em] shadow-lg shadow-black/20 hover:shadow-xl transition-shadow ${sizing} ${className}`}
    >
      Reserve Your Seat <ArrowRight size={size === "lg" ? 18 : 16} />
    </a>
  );
};

const LEARN = [
  { icon: MousePointerClick, t: "Navigate the platform", b: "Get oriented in ChatGPT with a calm, guided walkthrough. No prior AI experience required." },
  { icon: ShieldCheck, t: "Review important privacy settings", b: "Understand what to turn on, what to turn off, and how to keep your information protected." },
  { icon: Sparkles, t: "Understand its core features", b: "See how ChatGPT can help with everyday questions, drafts, summaries, and next-step planning." },
  { icon: MessageSquare, t: "Complete your first safe, useful chat", b: "Leave the session with a real conversation you started with confidence, not confusion." },
];

export default function FirstClickToFirstChat() {
  return (
    <div data-testid="page-first-click-to-first-chat">
      {/* Sticky-feel event banner strip */}
      <div className="w-full text-center py-2.5 text-[11px] md:text-xs font-semibold uppercase tracking-[0.22em]" style={{ backgroundColor: "#0A152C", color: "#E6C871" }} data-testid="event-banner">
        <span className="mx-2">Complimentary Live Workshop</span>
        <span className="mx-2 opacity-50">·</span>
        <span className="mx-2">{EVENT_DATE}</span>
        <span className="mx-2 opacity-50">·</span>
        <span className="mx-2">{EVENT_TIME}</span>
      </div>

      {/* Hero */}
      <section className="relative overflow-hidden" style={{ backgroundColor: "#0A152C" }}>
        <div
          className="absolute inset-0"
          style={{
            background:
              "radial-gradient(circle at 82% 20%, rgba(198,168,90,0.18), transparent 55%), radial-gradient(circle at 12% 82%, rgba(227,50,120,0.13), transparent 55%)",
          }}
          aria-hidden="true"
        />
        <div className="relative mx-auto max-w-6xl px-6 md:px-10 py-14 md:py-20">
          <div
            className="inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-[9px] uppercase tracking-[0.35em] font-semibold"
            style={{ backgroundColor: "#E6C871", color: "#0A152C" }}
            data-testid="event-badge"
          >
            <Video size={12} /> Live Workshop · Complimentary
          </div>
          <h1 className="mt-6 font-display-black text-[2rem] sm:text-4xl md:text-5xl lg:text-[3.5rem] leading-[1.05] text-white tracking-tight max-w-4xl">
            From First Click to First Chat:{" "}
            <span style={{ color: "#C6A85A" }}>Your Guided Introduction to ChatGPT</span>
            <span style={{ color: "#E33278" }}>.</span>
          </h1>

          {/* Who it is for (compact chip) */}
          <div className="mt-6 inline-flex items-center gap-2.5 rounded-full border border-white/25 px-5 py-2.5 text-base md:text-lg text-white/90 max-w-full" data-testid="who-its-for">
            <Users size={18} className="text-[var(--layor-gold)] shrink-0" />
            <span className="font-medium">For beginners, curious professionals, and leaders taking a calm first step into ChatGPT</span>
          </div>

          <p className="mt-6 text-white/85 text-base md:text-lg leading-relaxed max-w-3xl">
            Join LAYOR Digital Group for a welcoming, beginner-friendly introduction to ChatGPT.
            During this live, guided session, you will learn how to navigate the platform, review
            important privacy settings, understand its core features, and complete your first safe,
            useful chat with confidence.
          </p>

          {/* Event facts */}
          <div className="mt-9 grid sm:grid-cols-3 gap-3 md:gap-4 max-w-4xl">
            <div className="rounded-2xl border p-4 md:p-5" style={{ borderColor: "rgba(198,168,90,0.35)", backgroundColor: "rgba(255,255,255,0.04)" }}>
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.28em] font-semibold" style={{ color: "#E6C871" }}>
                <CalendarDays size={14} /> Date
              </div>
              <p className="mt-2 text-white text-sm md:text-base font-semibold" data-testid="event-date">
                {EVENT_DATE}
              </p>
            </div>
            <div className="rounded-2xl border p-4 md:p-5" style={{ borderColor: "rgba(198,168,90,0.35)", backgroundColor: "rgba(255,255,255,0.04)" }}>
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.28em] font-semibold" style={{ color: "#E6C871" }}>
                <Clock size={14} /> Time (Eastern)
              </div>
              <p className="mt-2 text-white text-sm md:text-base font-semibold" data-testid="event-time">
                {EVENT_TIME}
              </p>
            </div>
            <div className="rounded-2xl border p-4 md:p-5" style={{ borderColor: "rgba(198,168,90,0.35)", backgroundColor: "rgba(255,255,255,0.04)" }}>
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.28em] font-semibold" style={{ color: "#E6C871" }}>
                <Video size={14} /> Format
              </div>
              <p className="mt-2 text-white text-sm md:text-base font-semibold" data-testid="event-format">
                Live via Zoom · Complimentary
              </p>
            </div>
          </div>

          {/* Prominent primary CTA */}
          <div className="mt-10 md:mt-12 flex flex-col sm:flex-row sm:items-center gap-5">
            <ReserveButton testid="hero-reserve-your-seat" size="lg" />
            <p className="text-base md:text-lg text-white/85 max-w-md leading-relaxed font-medium">
              Registration required. Secured through Zoom. Seats are limited.
            </p>
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-[3px] flex">
          <div className="flex-[4]" style={{ backgroundColor: "#C6A85A" }} />
          <div className="flex-1" style={{ backgroundColor: "#E33278" }} />
        </div>
      </section>

      {/* What you will learn */}
      <Section
        eyebrow="What you will learn"
        title="Four practical outcomes in one guided hour."
        kicker="No jargon. No pressure. A calm, hands-on hour that leaves you with confidence and a first real chat you started yourself."
      >
        <div className="grid md:grid-cols-2 gap-4 md:gap-5">
          {LEARN.map(({ icon: Icon, t, b }, i) => (
            <div
              key={t}
              className="rounded-2xl border border-[var(--layor-border)] bg-white p-6"
              data-testid={`learn-${i + 1}`}
            >
              <div className="flex items-center gap-3">
                <div className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-[var(--layor-navy)] text-[var(--layor-gold)]">
                  <Icon size={18} />
                </div>
                <h3 className="font-display text-lg text-[var(--layor-navy)] leading-snug">{t}</h3>
              </div>
              <p className="mt-3 text-sm text-[var(--layor-muted)] leading-relaxed">{b}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* Who is hosting */}
      <Section
        eyebrow="Your host"
        title="Presented by LAYOR Digital Group."
        kicker="LAYOR Digital Group is a premium executive advisory and transformation firm led by Patricia Royal, Ed. S. With 30 plus years of leadership experience and a leadership-first approach to practical AI, LAYOR helps professionals and organizations use technology with clarity, confidence, and human judgment at the center."
        className="bg-white border-y border-[var(--layor-border)]"
      >
        <div className="grid md:grid-cols-3 gap-4 md:gap-5">
          <Card>
            <div className="eyebrow" style={{ color: "#C6A85A" }}>Trusted framework</div>
            <p className="mt-3 text-sm text-[var(--layor-muted)] leading-relaxed">
              Diagnose before we design. Strategize before we build. Execute with absolute
              discipline. The same principles that guide LAYOR engagements shape this workshop.
            </p>
          </Card>
          <Card>
            <div className="eyebrow" style={{ color: "#E33278" }}>Safety first</div>
            <p className="mt-3 text-sm text-[var(--layor-muted)] leading-relaxed">
              We cover privacy settings and safe practices before anyone types their first prompt,
              so you leave protected as well as prepared.
            </p>
          </Card>
          <Card>
            <div className="eyebrow" style={{ color: "#0A152C" }}>Real practice</div>
            <p className="mt-3 text-sm text-[var(--layor-muted)] leading-relaxed">
              This is not a slide-only session. You will actually complete your first safe, useful
              chat during the hour, guided the whole way.
            </p>
          </Card>
        </div>
      </Section>

      {/* Final CTA strip */}
      <section className="relative overflow-hidden" style={{ backgroundColor: "#0A152C" }}>
        <div className="mx-auto max-w-5xl px-6 md:px-10 py-14 md:py-18 text-center">
          <div className="text-[10px] uppercase tracking-[0.35em] font-semibold" style={{ color: "#E6C871" }}>
            Ready when you are
          </div>
          <h2 className="mt-4 font-display text-2xl sm:text-3xl md:text-4xl leading-[1.1] text-white">
            Reserve your complimentary seat for {EVENT_DATE}.
          </h2>
          <p className="mt-4 text-white/80 max-w-2xl mx-auto text-sm md:text-base leading-relaxed">
            {EVENT_TIME}. Live via Zoom. Registration required and seats are limited.
          </p>
          <div className="mt-8 flex justify-center">
            <ReserveButton testid="final-reserve-your-seat" size="lg" />
          </div>
          <p className="mt-6 text-xs text-white/60 max-w-xl mx-auto">
            Registration and confirmations are handled securely by Zoom. LAYOR Digital Group does
            not collect duplicate registration information on this page.
          </p>
        </div>
      </section>
    </div>
  );
}
